/**
 * Eigen documentsoorten (2026-11-05).
 *
 * Een gebruiker die mag uploaden (DOCUMENTEN.canEdit) kan in het upload-
 * formulier een documentsoort toevoegen die nog niet in de vaste lijst staat,
 * bijvoorbeeld "Vrijwaringsbewijs". De soort staat daarna voor iedereen in de
 * keuzelijst.
 *
 * Een eigen soort is een VERFIJNING van een vast basistype (standaard
 * ALGEMEEN_DOCUMENT): het document krijgt dat basistype als `documenttype`, zodat
 * de standaardrechten, de bibliotheek-adapters en de tellers ongewijzigd blijven
 * werken. De soort draagt wel zijn eigen standaard-zichtbaarheid (gekozen bij
 * toevoegen), die de maximum-regel van FO-383 volgt zoals een typestandaard.
 */
import { z } from 'zod'
import { idParam } from './id-validators'
import { UPLOAD_CONTEXT_SOORTEN, type UploadContextSoort } from './document-metadata-voorstel'
import { BIBLIOTHEEK_DOCUMENT_TYPE_LABELS, type DocumentType, type DocumentZichtbaarheid } from './documenten'

/** Het basistype van elke eigen soort (vast; uitbreiden = bewuste wijziging). */
export const EIGEN_DOCUMENTSOORT_BASISTYPE = 'ALGEMEEN_DOCUMENT' as const satisfies DocumentType

/**
 * Zichtbaarheden die je bij het toevoegen van een soort kunt kiezen. Bewust de
 * niveaus die zonder extra gegevens werken: ROLLEN/GEBRUIKERS vragen een lijst,
 * GEKOPPELDE_* hangt van de koppeling per document af en PRIVE is per uploader.
 */
export const EIGEN_DOCUMENTSOORT_ZICHTBAARHEDEN = [
  'INTERN',
  'HR_ONLY',
  'FINANCE_ONLY',
  'DIRECTIE_ONLY',
] as const satisfies readonly DocumentZichtbaarheid[]
export type EigenDocumentSoortZichtbaarheid = (typeof EIGEN_DOCUMENTSOORT_ZICHTBAARHEDEN)[number]

export const EIGEN_DOCUMENTSOORT_ZICHTBAARHEID_LABELS: Record<EigenDocumentSoortZichtbaarheid, string> = {
  INTERN: 'Intern STREEP (iedereen met toegang tot documenten)',
  HR_ONLY: 'Alleen HR',
  FINANCE_ONLY: 'Alleen Finance',
  DIRECTIE_ONLY: 'Alleen Directie',
}

export const EIGEN_DOCUMENTSOORT_NAAM_MAX = 80

/** Normalisatie voor de uniek-check: getrimd, enkele spaties, kleine letters. */
export function normaliseerSoortNaam(naam: string): string {
  return naam.trim().replace(/\s+/g, ' ').toLocaleLowerCase('nl-NL')
}

export const createEigenDocumentSoortSchema = z.object({
  naam: z
    .string()
    .transform((v) => v.trim().replace(/\s+/g, ' '))
    .pipe(
      z
        .string()
        .min(2, 'Vul een naam in van minstens 2 tekens')
        .max(EIGEN_DOCUMENTSOORT_NAAM_MAX, `De naam mag maximaal ${EIGEN_DOCUMENTSOORT_NAAM_MAX} tekens zijn`),
    ),
  zichtbaarheid: z.enum(EIGEN_DOCUMENTSOORT_ZICHTBAARHEDEN).default('INTERN'),
  /** De upload-plek waar de soort is aangemaakt; daar staat hij bovenaan. */
  voorkeurContext: z.enum(UPLOAD_CONTEXT_SOORTEN).optional(),
})
export type CreateEigenDocumentSoortInput = z.input<typeof createEigenDocumentSoortSchema>

export interface EigenDocumentSoortDTO {
  id: string
  naam: string
  basistype: DocumentType
  zichtbaarheid: DocumentZichtbaarheid
  voorkeurContext: UploadContextSoort | null
  /** Aantal (niet-verwijderde) documenten van deze soort. */
  aantalDocumenten: number
}

/** Antwoord op toevoegen: `bestond` = er was al een soort met deze naam. */
export interface CreateEigenDocumentSoortResultDTO {
  soort: EigenDocumentSoortDTO
  bestond: boolean
}

/** Id-veld voor upload/edit/filter. */
export const eigenSoortIdRef = idParam('eigenSoortId')

/**
 * Keuzelijst-waarde van een eigen soort in een `<select>` naast de vaste typen
 * (die hun enum-waarde houden). Eén plek voor het voorvoegsel.
 */
export const EIGEN_SOORT_OPTIE_PREFIX = 'eigen:'
export function eigenSoortOptieWaarde(id: string): string {
  return `${EIGEN_SOORT_OPTIE_PREFIX}${id}`
}
export function eigenSoortIdUitOptie(waarde: string): string | null {
  return waarde.startsWith(EIGEN_SOORT_OPTIE_PREFIX)
    ? waarde.slice(EIGEN_SOORT_OPTIE_PREFIX.length) || null
    : null
}

/**
 * Eigen soorten in keuzelijst-volgorde: die van de huidige upload-plek eerst,
 * daarna de rest, elk alfabetisch.
 */
export function sorteerEigenSoorten<T extends { naam: string; voorkeurContext: UploadContextSoort | null }>(
  soorten: readonly T[],
  context: UploadContextSoort | null,
): T[] {
  const opNaam = (a: T, b: T) => a.naam.localeCompare(b.naam, 'nl')
  const hier = soorten.filter((s) => context !== null && s.voorkeurContext === context).sort(opNaam)
  const rest = soorten.filter((s) => !(context !== null && s.voorkeurContext === context)).sort(opNaam)
  return [...hier, ...rest]
}

/**
 * Het label van de soort van een document: de eigen soort als die er is, anders
 * het label van het vaste documenttype (null = geen type).
 */
export function documentSoortLabel(doc: {
  documenttype: DocumentType | null
  eigenSoortNaam?: string | null
}): string | null {
  if (doc.eigenSoortNaam) return doc.eigenSoortNaam
  return doc.documenttype ? BIBLIOTHEEK_DOCUMENT_TYPE_LABELS[doc.documenttype] : null
}

function normaliseerVoorBestandsmatch(s: string): string {
  return s
    .toLocaleLowerCase('nl-NL')
    .replace(/\.[a-z0-9]{1,5}$/i, '')
    .replace(/[_\-.()[\]]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Herken een eigen soort in een bestandsnaam ("Vrijwaringsbewijs VVB-75-X.pdf"
 * -> "Vrijwaringsbewijs"): de genormaliseerde soortnaam moet als hele woorden
 * in de bestandsnaam staan. Meerdere treffers: de langste naam wint (de meest
 * specifieke). Geen treffer = null (dan blijft het type een keuze).
 */
export function herkenEigenSoortInBestandsnaam<T extends { naam: string }>(
  bestandsnaam: string,
  soorten: readonly T[],
): T | null {
  const doel = ` ${normaliseerVoorBestandsmatch(bestandsnaam)} `
  let beste: T | null = null
  let besteLengte = 0
  for (const soort of soorten) {
    const naam = normaliseerVoorBestandsmatch(soort.naam)
    if (naam.length < 3) continue
    if (doel.includes(` ${naam} `) && naam.length > besteLengte) {
      beste = soort
      besteLengte = naam.length
    }
  }
  return beste
}
